import { useState, useEffect } from "react";
import axios from "axios";
import { API } from "../App";
import DashboardLayout from "../components/DashboardLayout";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { FileText, ExternalLink, Download, BookOpen, CheckCircle2, Lightbulb } from "lucide-react";
import { toast } from "sonner";

const DiscoverCanada = ({ auth }) => {
  const [pdfInfo, setPdfInfo] = useState(null);
  const [chapters, setChapters] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchPdfInfo();
    fetchChapters();
  }, []);

  const fetchPdfInfo = async () => {
    try {
      const response = await axios.get(`${API}/discover-canada-pdf`);
      setPdfInfo(response.data);
    } catch (error) {
      console.error("Error fetching PDF info:", error);
      // Fallback to official URL if endpoint fails
      setPdfInfo({
        pdf_url: "https://www.canada.ca/content/dam/ircc/migration/ircc/english/pdf/pub/discover.pdf",
        title: "Discover Canada"
      });
    } finally {
      setLoading(false);
    }
  };

  const fetchChapters = async () => {
    try {
      const response = await axios.get(`${API}/chapters`);
      setChapters(response.data);
    } catch (error) {
      console.error("Error fetching chapters:", error);
    }
  };

  const openPdfInNewTab = () => {
    if (pdfInfo && pdfInfo.pdf_url) {
      window.open(pdfInfo.pdf_url, '_blank', 'noopener,noreferrer');
    } else {
      // Fallback to official Government of Canada URL
      window.open("https://www.canada.ca/content/dam/ircc/migration/ircc/english/pdf/pub/discover.pdf", '_blank', 'noopener,noreferrer');
    }
  };

  const downloadPdf = async () => {
    try {
      // Try to download from local server first
      const response = await axios.get(`${API}/discover-canada-pdf/download`, {
        responseType: 'blob'
      });
      
      // Create download link
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', 'Discover_Canada.pdf');
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      toast.success("Download started!");
    } catch (error) {
      // Fallback: open the official PDF URL for download
      window.open("https://www.canada.ca/content/dam/ircc/migration/ircc/english/pdf/pub/discover.pdf", '_blank');
      toast.info("Opening official PDF in new tab");
    }
  };

  if (loading) {
    return (
      <DashboardLayout auth={auth}>
        <div className="flex items-center justify-center h-64">
          <div className="w-12 h-12 border-4 border-maple border-t-transparent rounded-full animate-spin" />
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout auth={auth}>
      <div className="space-y-8" data-testid="discover-canada-page">
        {/* Header */}
        <div>
          <h1 className="font-heading text-3xl font-bold text-slate-900 mb-2">Discover Canada</h1>
          <p className="text-slate-600">The official study guide for the Canadian Citizenship Test</p>
        </div>

        {/* Main PDF Card */}
        <Card className="border-2 border-truenorth/20 bg-gradient-to-br from-truenorth-50 to-blue-50">
          <CardContent className="p-8">
            <div className="flex flex-col md:flex-row items-center gap-8">
              <div className="w-32 h-40 bg-truenorth rounded-lg flex items-center justify-center flex-shrink-0 shadow-lg">
                <FileText className="w-16 h-16 text-white" />
              </div>
              <div className="flex-1 text-center md:text-left">
                <h2 className="font-heading text-2xl font-bold text-slate-900 mb-2">
                  Discover Canada: The Rights and Responsibilities of Citizenship
                </h2>
                <p className="text-slate-600 mb-6 max-w-2xl">
                  This is the official study guide published by the Government of Canada. It contains all the information 
                  you need to know for the citizenship test, including Canadian history, government, rights, and responsibilities.
                </p>
                <div className="flex flex-col sm:flex-row gap-3 justify-center md:justify-start">
                  <Button 
                    onClick={openPdfInNewTab}
                    size="lg"
                    className="bg-truenorth hover:bg-truenorth-600 text-white"
                    data-testid="read-pdf-btn"
                  >
                    <ExternalLink className="w-5 h-5 mr-2" />
                    Read Online
                  </Button>
                  <Button 
                    onClick={downloadPdf}
                    variant="outline" 
                    size="lg" 
                    data-testid="download-pdf-btn"
                  >
                    <Download className="w-5 h-5 mr-2" />
                    Download PDF
                  </Button>
                </div>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Chapter Overview */}
        <div>
          <h2 className="font-heading text-xl font-bold text-slate-900 mb-4">What's Inside</h2>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
            {chapters.map((chapter, index) => (
              <Card 
                key={chapter.chapter_id || index}
                className="border border-slate-200 hover:border-maple/30 transition-colors"
              >
                <CardContent className="p-4">
                  <div className="flex items-start gap-3">
                    <div className="w-8 h-8 bg-maple-50 rounded-lg flex items-center justify-center text-maple flex-shrink-0">
                      <span className="font-heading font-bold text-sm">{chapter.order || index + 1}</span>
                    </div>
                    <div>
                      <h3 className="font-heading font-semibold text-slate-900 text-sm">
                        {chapter.title}
                      </h3>
                      <p className="text-xs text-slate-500 mt-1 line-clamp-2">
                        {chapter.summary || chapter.description}
                      </p>
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>

        {/* Study Tips */}
        <Card className="border-0 bg-gradient-to-br from-yellow-50 to-orange-50">
          <CardHeader>
            <CardTitle className="font-heading text-lg flex items-center gap-2">
              <Lightbulb className="w-5 h-5 text-yellow-600" />
              Study Tips
            </CardTitle>
          </CardHeader>
          <CardContent className="grid md:grid-cols-2 gap-4">
            <div className="flex items-start gap-3">
              <CheckCircle2 className="w-5 h-5 text-green-600 flex-shrink-0 mt-0.5" />
              <p className="text-sm text-slate-700">Read through the entire guide at least once before starting practice tests</p>
            </div>
            <div className="flex items-start gap-3">
              <CheckCircle2 className="w-5 h-5 text-green-600 flex-shrink-0 mt-0.5" />
              <p className="text-sm text-slate-700">Pay special attention to dates, names, and historical events</p>
            </div>
            <div className="flex items-start gap-3">
              <CheckCircle2 className="w-5 h-5 text-green-600 flex-shrink-0 mt-0.5" />
              <p className="text-sm text-slate-700">Use the flashcards to memorize key facts after reading each chapter</p>
            </div>
            <div className="flex items-start gap-3">
              <CheckCircle2 className="w-5 h-5 text-green-600 flex-shrink-0 mt-0.5" />
              <p className="text-sm text-slate-700">Take practice tests to identify your weak areas</p>
            </div>
          </CardContent>
        </Card>
      </div>
    </DashboardLayout>
  );
};

export default DiscoverCanada;
